"use client";

import { useEffect } from "react";

// =============================================================================
// BuildFreshnessGuard — hard-reload when this tab is running a stale build.
// =============================================================================
//
// Production incident (2026-07-05): the reactivate-plan flow leaves the site
// via `window.location.href = <Stripe checkout URL>` (SchedulerClient
// startUpgrade). When the user clicks the browser Back button, the browser
// restores the page from the back-forward cache (bfcache): the OLD build's
// DOM, JS heap, and hashed asset URLs — all still served by Vercel, which
// keeps previous deployments' immutable assets available. From that restored
// page every client-side navigation fetches the OLD build's RSC payloads, so
// after a redeploy the user tours the entire pre-redesign UI until a hard
// reload. The same skew hits any long-lived tab that regains focus days
// after a deploy.
//
// Fix: next.config.ts bakes NEXT_PUBLIC_BUILD_STAMP into the client bundle
// at build time, and /api/build returns the running server's stamp with
// Cache-Control: no-store. This component compares the two:
//
//   - on `pageshow` with event.persisted === true (bfcache restore): ALWAYS
//     check — this is the exact moment the Stripe-Back bug manifests;
//   - on `visibilitychange` → visible (long-lived tab refocused): check,
//     throttled to at most once per CHECK_THROTTLE_MS.
//
// On mismatch it calls window.location.reload(), which the browser performs
// as a revalidating navigation → fresh HTML → fresh asset URLs → new build.
//
// Guard rails:
//   - Never loops: before reloading it records the mismatch pair in
//     sessionStorage; if that exact pair was already the reason for a
//     reload in this tab, it never reloads again. (Normally the reload
//     fetches the new build, the stamps then match, and the record is
//     irrelevant — the record only matters if some broken cache keeps
//     serving stale HTML after a reload.)
//   - Silently no-ops on any fetch/parse failure (offline, 500, proxy).
//   - Zero hydration impact: renders null, all logic inside useEffect.
//   - A reload only fires when the deployed build genuinely changed, which
//     already invalidated whatever session the stale page had.
export const CHECK_THROTTLE_MS = 5 * 60 * 1000;
const RELOADED_FOR_KEY = "ranksector.buildReloadedFor";

export default function BuildFreshnessGuard() {
  useEffect(() => {
    const clientStamp = process.env.NEXT_PUBLIC_BUILD_STAMP;
    // Stamp missing means next.config didn't bake one (e.g. some test
    // harness) — do nothing rather than guess.
    if (!clientStamp) return;

    // The JS heap survives bfcache restores, so these persist across
    // pageshow(persisted) — exactly what the throttle needs. Start the
    // clock at mount: the page was just served, it cannot be stale yet.
    let lastCheckedAt = Date.now();
    let inFlight = false;

    async function checkFreshness(force: boolean) {
      const now = Date.now();
      if (inFlight) return;
      if (!force && now - lastCheckedAt < CHECK_THROTTLE_MS) return;
      inFlight = true;
      lastCheckedAt = now;
      try {
        const res = await fetch("/api/build", {
          cache: "no-store",
          credentials: "same-origin",
        });
        if (!res.ok) return;
        const data: unknown = await res.json();
        const serverStamp =
          data && typeof data === "object" && "stamp" in data
            ? (data as { stamp: unknown }).stamp
            : null;
        if (typeof serverStamp !== "string" || serverStamp.length === 0) return;
        if (serverStamp === clientStamp) return;

        // No-loop guard: one reload per (old build → new build) pair per tab.
        const mismatch = `${clientStamp}->${serverStamp}`;
        try {
          if (sessionStorage.getItem(RELOADED_FOR_KEY) === mismatch) return;
          sessionStorage.setItem(RELOADED_FOR_KEY, mismatch);
        } catch {
          // sessionStorage unavailable (rare, e.g. storage disabled). The
          // reload itself revalidates the HTML so a loop still can't form
          // under a correctly behaving cache; proceed.
        }
        window.location.reload();
      } catch {
        // Network failure / malformed body — silently keep the page alive.
      } finally {
        inFlight = false;
      }
    }

    const onPageShow = (event: PageTransitionEvent) => {
      // persisted === true ⇔ restored from bfcache. This is the Stripe
      // checkout Back-button path; always check, bypassing the throttle.
      if (event.persisted) void checkFreshness(true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void checkFreshness(false);
    };

    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return null;
}
